import type { HastLike } from "@/lib/bidi.ts";

/** The text of a hast subtree, as a reader sees it (a <br> is a line break). */
export function hastText(node: HastLike | undefined): string {
  if (!node) return "";
  if (node.type === "text") return node.value ?? "";
  if (node.tagName === "br") return "\n";
  return (node.children ?? []).map(hastText).join("");
}

function rowsOf(node: HastLike): HastLike[] {
  const out: HastLike[] = [];
  for (const c of node.children ?? []) {
    if (c.tagName === "tr") out.push(c);
    else if (c.tagName === "thead" || c.tagName === "tbody" || c.tagName === "tfoot") out.push(...rowsOf(c));
  }
  return out;
}

/** A table as tab-separated text: pastes into Excel, Numbers and Google Sheets as cells. */
export function tableTsv(table: HastLike | undefined): string {
  if (!table) return "";
  return rowsOf(table)
    .map((tr) => (tr.children ?? []).filter((c) => c.tagName === "th" || c.tagName === "td").map((c) => hastText(c).replace(/\s+/g, " ").trim()).join("\t"))
    .join("\n");
}

// currency names and signs an amount may carry, Arabic and English
const MONEY = /دولار(?:ًا|ا)?|ريال|درهم|دينار|جنيه|ليرة|يورو|USD|EUR|SAR|AED|JOD|EGP|TRY|GBP|[$€£¥₺]/gi;

/** Whether a cell holds an amount, a count or a range ("1,200 $", "≈ 4,500", "دولار 1,350–2,300", "9", "25%"),
 *  so its column lines its digits up. Dates and words are not amounts. */
export function isNumericCell(text: string): boolean {
  const t = text.replace(MONEY, "").trim();
  if (!t || !/[\d٠-٩]/.test(t)) return false;
  if (/\d[/.-]\d{1,2}[/.-]\d/.test(t)) return false; // a date
  return /^[\s\d.,٠-٩٫٬%+\-–—~≈×x()/]+$/i.test(t);
}

/** A bare link shown short: host and path, without the scheme, "www." or a trailing slash. */
export function shortUrl(href: string): { host: string; path: string } | null {
  let u: URL;
  try { u = new URL(href); } catch { return null; }
  if (u.protocol !== "http:" && u.protocol !== "https:") return null;
  let path = decodeURIComponent(u.pathname + u.search + u.hash);
  if (path === "/") path = "";
  return { host: u.host.replace(/^www\./, ""), path: path.replace(/\/$/, "") };
}

/** Whether a link's text is its own address (a bare URL the agent pasted), so it can be shown as a short chip. */
export function isBareLink(href: string | undefined, text: string): boolean {
  if (!href) return false;
  const strip = (s: string) => s.trim().replace(/^https?:\/\//i, "").replace(/^www\./, "").replace(/\/$/, "");
  return /^https?:\/\//i.test(href) && strip(href) === strip(text);
}

export const CALLOUTS = ["note", "tip", "important", "warning", "caution"] as const;
export type CalloutKind = (typeof CALLOUTS)[number];

/** Just enough of an mdast node for the callout plugin. */
interface MdastLike {
  type: string;
  value?: string;
  children?: MdastLike[];
  data?: { hProperties?: Record<string, unknown> };
}

/**
 * GitHub's alerts: a quote that opens with [!NOTE], [!TIP], [!IMPORTANT], [!WARNING] or [!CAUTION] becomes a
 * callout (the marker is removed; the kind is handed to the blockquote component as data-callout).
 */
export function remarkCallouts() {
  const walk = (node: MdastLike) => {
    if (node.type === "blockquote") mark(node);
    for (const c of node.children ?? []) walk(c);
  };
  return (tree: MdastLike) => walk(tree);
}

function mark(quote: MdastLike): void {
  const para = quote.children?.[0];
  const first = para?.type === "paragraph" ? para.children?.[0] : undefined;
  if (!para || first?.type !== "text" || !first.value) return;
  const m = /^\s*\[!(note|tip|important|warning|caution)\][ \t]*\n?/i.exec(first.value);
  if (!m) return;
  first.value = first.value.slice(m[0].length);
  const kids = para.children!;
  if (!first.value) kids.shift();
  // with remark-breaks the line after the marker starts with a break
  while (kids[0]?.type === "break" || (kids[0]?.type === "text" && !kids[0].value?.trim())) kids.shift();
  if (!kids.length) quote.children!.shift();
  quote.data = { ...quote.data, hProperties: { ...quote.data?.hProperties, dataCallout: m[1]!.toLowerCase() } };
}

interface HastEl extends HastLike {
  properties?: Record<string, unknown>;
  children?: HastEl[];
}

function cellsOf(tr: HastEl): HastEl[] {
  return (tr.children ?? []).filter((c) => c.tagName === "th" || c.tagName === "td");
}

/** Marks the columns of a table that hold amounts (every filled body cell is one), so their digits line up. */
export function rehypeNumericColumns() {
  const walk = (node: HastEl) => {
    if (node.tagName === "table") markTable(node);
    else for (const c of node.children ?? []) walk(c);
  };
  return (tree: HastEl) => walk(tree);
}

function markTable(table: HastEl): void {
  const rows = rowsOf(table) as HastEl[];
  const body = rows.filter((tr) => cellsOf(tr).every((c) => c.tagName === "td"));
  const width = Math.max(0, ...rows.map((tr) => cellsOf(tr).length));
  for (let col = 0; col < width; col++) {
    const texts = body.map((tr) => hastText(cellsOf(tr)[col]).trim()).filter(Boolean);
    if (!texts.length || !texts.every(isNumericCell)) continue;
    for (const tr of rows) {
      const cell = cellsOf(tr)[col];
      if (!cell) continue;
      const cls = cell.properties?.className;
      cell.properties = { ...cell.properties, className: [...(Array.isArray(cls) ? cls : []), "num"] };
    }
  }
}
